import React, { useRef, useState } from "react";
import {
  X, Printer, Loader2, AlertCircle, RefreshCw, SlidersHorizontal, XCircle,
} from "lucide-react";
// The report body moved to AnalysisReportView. Re-exported here so the page
// and the modal below keep importing it from one place.
import { AnalysisReport } from "./AnalysisReportView";
import {
  useGetApplicationsAnalysisQuery,
  useGetStatesQuery,
  useGetChaptersQuery,
  useGetSectorsQuery,
} from "../api/probationerApplicationApi";

// Kept below the imports: `export … from` is a statement, so re-exporting
// above them puts an import in the module body and trips `import/first`.
export { AnalysisReport } from "./AnalysisReportView";

/* ------------------------------------------------------------------ */
/* Types                                                                */
/* ------------------------------------------------------------------ */
export type AnalysisScope =
  | { kind: "diet"; dietId: string; label: string }
  | { kind: "category"; dietId: string; categoryId: number; dietLabel: string; categoryLabel: string };

export type AnalysisFilters = {
  status:     string;
  year:       string;
  month:      string;
  age_range:  string;
  gender:     string;
  whois:      string;
  state_id:   string;
  chapter_id: string;
  sector_id:  string;
};

export const EMPTY_FILTERS: AnalysisFilters = {
  status: "", year: "", month: "", age_range: "",
  gender: "", whois: "", state_id: "", chapter_id: "", sector_id: "",
};

/* ------------------------------------------------------------------ */
/* Filter constants                                                     */
/* ------------------------------------------------------------------ */
const STATUS_OPTIONS = ["pending", "acknowledged", "submitted", "approved", "rejected"];
const AGE_OPTIONS    = ["18-24","25-29","30-34","35-39","40-44","45-49","50-54","55-59","60+"];
const GENDER_OPTIONS = ["male", "female"];
const WHOIS_OPTIONS  = ["probationer", "student", "technician", "graduate"];
const MONTH_OPTIONS  = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const YEAR_OPTIONS = Array.from({ length: 9 }, (_, i) => String(new Date().getFullYear() - i));

// backend whereMonth() expects 1-12
export const MONTH_TO_NUM: Record<string, string> = {
  January:"1", February:"2", March:"3", April:"4", May:"5", June:"6",
  July:"7", August:"8", September:"9", October:"10", November:"11", December:"12",
};

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */
function cap(s: string) { return s.charAt(0).toUpperCase() + s.slice(1); }

/* ------------------------------------------------------------------ */
/* Filter bar atoms                                                     */
/* ------------------------------------------------------------------ */
function FilterSelect({
  label, value, options, onChange,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex items-center gap-1.5">
      <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 shrink-0">{label}</span>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`text-[11px] font-semibold bg-white border rounded-lg pl-2.5 pr-6 py-1.5 outline-none transition-all appearance-none cursor-pointer ${
            value ? "border-indigo-400 text-indigo-700 bg-indigo-50" : "border-gray-200 text-gray-600 hover:border-gray-400"
          }`}
        >
          <option value="">All</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 text-[9px]">▾</span>
      </div>
    </label>
  );
}

type FilterBarProps = {
  filters: AnalysisFilters;
  onChange: (f: AnalysisFilters) => void;
  states:   Array<{ id: number; name: string }>;
  chapters: Array<{ id: number; name: string }>;
  sectors:  Array<{ id: number; name: string }>;
};

export function FilterBar({ filters, onChange, states, chapters, sectors }: FilterBarProps) {
  const activeCount = Object.values(filters).filter(Boolean).length;

  const set = <K extends keyof AnalysisFilters>(key: K) => (v: string) =>
    onChange({ ...filters, [key]: v });

  return (
    <div className="px-6 py-3 bg-gray-50/80 border-b border-gray-100 no-print">
      {/* Row 1 */}
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <div className="flex items-center gap-1.5 text-gray-500 shrink-0">
          <SlidersHorizontal className="w-3.5 h-3.5" />
          <span className="text-[10px] font-black uppercase tracking-widest">Filters</span>
        </div>
        <FilterSelect
          label="Status" value={filters.status}
          options={STATUS_OPTIONS.map((s) => ({ value: s, label: cap(s) }))}
          onChange={set("status")}
        />
        <FilterSelect
          label="Gender" value={filters.gender}
          options={GENDER_OPTIONS.map((g) => ({ value: g, label: cap(g) }))}
          onChange={set("gender")}
        />
        <FilterSelect
          label="Category" value={filters.whois}
          options={WHOIS_OPTIONS.map((w) => ({ value: w, label: cap(w) }))}
          onChange={set("whois")}
        />
        <FilterSelect
          label="Age Range" value={filters.age_range}
          options={AGE_OPTIONS.map((a) => ({ value: a, label: a }))}
          onChange={set("age_range")}
        />
        <FilterSelect
          label="Year" value={filters.year}
          options={YEAR_OPTIONS.map((y) => ({ value: y, label: y }))}
          onChange={set("year")}
        />
        <FilterSelect
          label="Month" value={filters.month}
          options={MONTH_OPTIONS.map((m) => ({ value: m, label: m }))}
          onChange={set("month")}
        />
        {activeCount > 0 && (
          <button
            onClick={() => onChange(EMPTY_FILTERS)}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-600 text-[10px] font-black uppercase tracking-wider hover:bg-red-100 transition-all"
          >
            <XCircle className="w-3.5 h-3.5" />
            Clear {activeCount} filter{activeCount > 1 ? "s" : ""}
          </button>
        )}
      </div>
      {/* Row 2 — location-based */}
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[10px] font-black uppercase tracking-widest text-gray-300 shrink-0 min-w-[44px]">Scope</span>
        <FilterSelect
          label="State" value={filters.state_id}
          options={states.map((s) => ({ value: String(s.id), label: s.name }))}
          onChange={set("state_id")}
        />
        <FilterSelect
          label="Chapter" value={filters.chapter_id}
          options={chapters.map((c) => ({ value: String(c.id), label: c.name }))}
          onChange={set("chapter_id")}
        />
        <FilterSelect
          label="Sector" value={filters.sector_id}
          options={sectors.map((s) => ({ value: String(s.id), label: s.name }))}
          onChange={set("sector_id")}
        />
      </div>
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* Print — opens a clean popup so overflow/flex constraints don't clip */
/* ------------------------------------------------------------------ */
export function printRef(el: HTMLDivElement | null, title: string) {
  if (!el) return;

  const pw = window.open("", "_blank");
  if (!pw) {
    alert("Pop-ups are blocked. Please allow pop-ups for this site to print.");
    return;
  }

  // Copy every <style> and <link rel=stylesheet> from the host page.
  // .href on an HTMLLinkElement returns the absolute URL, so it resolves
  // correctly even when the href was originally relative.
  const styleContent = Array.from(document.head.querySelectorAll("style"))
    .map((s) => `<style>${s.textContent}</style>`)
    .join("\n");

  const linkContent = Array.from(
    document.head.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'),
  )
    .map((l) => `<link rel="stylesheet" href="${l.href}">`)
    .join("\n");

  pw.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${title}</title>
  ${linkContent}
  ${styleContent}
  <style>
    body { background: white !important; padding: 32px; font-family: sans-serif; }
    @page  { margin: 1.5cm; }
    table  { page-break-inside: auto; border-collapse: collapse; width: 100%; }
    tr     { page-break-inside: avoid; page-break-after: auto; }
    thead  { display: table-header-group; }
    /* The report is a stack of section cards. Splitting one across a fold puts
       its rows on a page with no heading, so each is kept whole. */
    section { page-break-inside: avoid; break-inside: avoid; }
    /* Screen-only controls — the jump index and the "show all" toggles. Their
       rows are already rendered in full below, so the buttons would only
       mislead on paper. */
    .no-print { display: none !important; }
  </style>
</head>
<body>${el.innerHTML}</body>
</html>`);

  pw.document.close();
  pw.focus();
  // Give external stylesheet links time to load before calling print.
  // onload fires after all linked resources finish; setTimeout is a fallback
  // for browsers that fire onload before document.write content is parsed.
  pw.onload = () => pw.print();
  setTimeout(() => { try { pw.print(); } catch (_) {} }, 900);
}

/* ------------------------------------------------------------------ */
/* Modal wrapper                                                        */
/* ------------------------------------------------------------------ */
type Props = { scope: AnalysisScope; onClose: () => void };

export default function ApplicationAnalysisReport({ scope, onClose }: Props) {
  const printContentRef = useRef<HTMLDivElement>(null);
  const [filters, setFilters] = useState<AnalysisFilters>(EMPTY_FILTERS);

  const dietId     = scope.dietId;
  const categoryId = scope.kind === "category" ? scope.categoryId : undefined;

  const reportTitle =
    scope.kind === "diet"
      ? `Diet-Wide Analysis — ${scope.label}`
      : `${scope.categoryLabel} — ${scope.dietLabel}`;

  // Fetch options for location-based filters
  const { data: states   = [] } = useGetStatesQuery();
  const { data: chapters = [] } = useGetChaptersQuery();
  const { data: sectors  = [] } = useGetSectorsQuery();

  const { data, isLoading, isFetching, error, refetch } = useGetApplicationsAnalysisQuery({
    dietId,
    categoryId,
    status:     filters.status     || undefined,
    year:       filters.year       || undefined,
    // Convert month name → number for backend whereMonth()
    month:      filters.month      ? MONTH_TO_NUM[filters.month] : undefined,
    age_range:  filters.age_range  || undefined,
    gender:     filters.gender     || undefined,
    whois:      filters.whois      || undefined,
    state_id:   filters.state_id   || undefined,
    chapter_id: filters.chapter_id || undefined,
    sector_id:  filters.sector_id  || undefined,
  });

  const activeCount = Object.values(filters).filter(Boolean).length;

  const handlePrint = () => printRef(printContentRef.current, reportTitle);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl flex flex-col max-h-[97vh]">

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-900 rounded-t-2xl shrink-0 no-print">
          <div>
            <h2 className="font-black text-white text-base uppercase tracking-tight">
              Application Analysis Report
            </h2>
            <p className="text-xs text-gray-400 mt-0.5 font-medium">{reportTitle}</p>
            {activeCount > 0 && (
              <p className="text-[10px] text-indigo-400 font-bold mt-0.5">
                {activeCount} active filter{activeCount > 1 ? "s" : ""} applied
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              title="Refresh"
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 text-white ${isFetching ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={handlePrint}
              disabled={isLoading || !!error}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 text-white text-xs font-black uppercase tracking-widest hover:bg-indigo-500 disabled:opacity-50 transition-colors"
            >
              <Printer className="w-4 h-4" /> Print Report
            </button>
            <button onClick={onClose} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors">
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>

        {/* ── Filter bar ── */}
        <FilterBar
          filters={filters}
          onChange={setFilters}
          states={states}
          chapters={chapters}
          sectors={sectors}
        />

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto px-8 py-6" ref={printContentRef}>
          {/* Show faint overlay while refetching after filter change */}
          {isFetching && !isLoading && (
            <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-indigo-50 rounded-lg border border-indigo-100">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-500" />
              <span className="text-[11px] font-semibold text-indigo-600">Applying filters…</span>
            </div>
          )}
          {isLoading ? (
            <div className="py-32 flex flex-col items-center gap-4 text-gray-400">
              <Loader2 className="w-10 h-10 animate-spin text-indigo-500" />
              <p className="text-sm font-semibold">Generating analysis report…</p>
              <p className="text-xs text-gray-300">Aggregating application data across all fields</p>
            </div>
          ) : error ? (
            <div className="py-24 text-center space-y-5">
              <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mx-auto">
                <AlertCircle className="w-10 h-10 text-red-400" />
              </div>
              <div>
                <p className="font-bold text-gray-900 text-lg">Could not load analysis</p>
                <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">
                  {(error as any)?.data?.message || "The server could not generate the report. Please try again."}
                </p>
              </div>
              <button onClick={() => refetch()} className="px-6 py-3 rounded-xl bg-gray-900 text-white font-bold hover:bg-gray-800 transition-colors">
                Retry
              </button>
            </div>
          ) : (
            <AnalysisReport data={data} title={reportTitle} filters={filters} />
          )}
        </div>

        {/* ── Footer ── */}
        {!isLoading && !error && (
          <div className="px-6 py-2.5 border-t bg-gray-50 rounded-b-2xl shrink-0 no-print flex items-center justify-between">
            <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">NIQS Portal — Confidential</p>
            <p className="text-[10px] text-gray-400 font-medium">
              {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
